import {ExternalLink, FileText, Package, Truck, User} from "lucide-react";
import type {AdminOrderDetail} from "@zaaaz/shared";
import {Button} from "@/components/ui/button";
import {AdminTabbedModal} from "../ui/AdminTabbedModal";
import {OrderStatusBar} from "./OrderStatusBar";
import {OrderSymphonyaPanel} from "./OrderSymphonyaPanel";
import {OrderSummaryTab} from "./OrderSummaryTab";
import {OrderClientTab} from "./OrderClientTab";
import {OrderDeliveryTab} from "./OrderDeliveryTab";
import {OrderProductsTab} from "./OrderProductsTab";
import {OrderSection} from "./OrderSection";

type DeliveryForm = {
  awb: string;
  cost: string;
  adresa: string;
  adresa_localitate: string;
  adresa_codpostal: string;
  curier: number;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detail: AdminOrderDetail | null;
  deliveryForm: DeliveryForm;
  setDeliveryForm: (v: DeliveryForm) => void;
  onApplyStatus: (id: number, op: string, label: string) => void;
  onSaveDelivery: (id: number) => void | Promise<void>;
  onRemoveDiscount: (discountId: number, orderId: number) => void;
  onGenAwbSameday: (id: number) => void;
  onSaveLines: (id: number, lines: {productId: number; quantity: number}[]) => Promise<void>;
  onOpenOrder?: (id: number) => void;
  symphonyaSending?: boolean;
  onResendSymphonya?: () => void;
};

export function OrderDetailModal({
  open,
  onOpenChange,
  detail,
  deliveryForm,
  setDeliveryForm,
  onApplyStatus,
  onSaveDelivery,
  onRemoveDiscount,
  onGenAwbSameday,
  onSaveLines,
  onOpenOrder,
  symphonyaSending = false,
  onResendSymphonya
}: Props) {
  if(!detail) return null;

  const documentsTab = (
    <div className="space-y-4">
      <OrderSection title="Payment link" icon={<ExternalLink />}>
        {detail.paymentLink ? (
          <a
            href={detail.paymentLink}
            target="_blank"
            rel="noreferrer"
            className="break-all text-sm text-primary hover:underline"
          >
            {detail.paymentLink}
          </a>
        ) : (
          <p className="text-sm text-muted-foreground">No payment link for this order.</p>
        )}
      </OrderSection>
      <OrderSection title="Invoice" icon={<FileText />} dense>
        {detail.invoicePdfUrl ? (
          <Button variant="outline" size="sm" asChild>
            <a href={detail.invoicePdfUrl} target="_blank" rel="noreferrer">View invoice PDF</a>
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">No invoice PDF available.</p>
        )}
      </OrderSection>
    </div>
  );

  return (
    <AdminTabbedModal
      open={open}
      onOpenChange={onOpenChange}
      icon={Package}
      title={<>Order <span className="tabular-nums">#{detail.id}</span></>}
      description={(
        <span className="admin-modal__meta">
          <span className="admin-modal__meta-chip admin-modal__meta-chip--muted">{detail.client.nume}</span>
          <span className="admin-modal__meta-chip admin-modal__meta-chip--accent">{detail.total.toFixed(2)} lei</span>
          {detail.dateLabel ? <span className="admin-modal__meta-chip admin-modal__meta-chip--muted">{detail.dateLabel}</span> : null}
        </span>
      )}
      headerExtra={(
        <div className="space-y-3.5">
          {onResendSymphonya ? (
            <OrderSymphonyaPanel detail={detail} sending={symphonyaSending} onResend={onResendSymphonya} />
          ) : null}
          <OrderStatusBar detail={detail} onApplyStatus={onApplyStatus} />
        </div>
      )}
      defaultTab="summary"
      size="xl"
      tabs={[
        {id: "summary", label: "Summary", content: <OrderSummaryTab detail={detail} />},
        {
          id: "client",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              Client
            </span>
          ),
          content: <OrderClientTab detail={detail} onRemoveDiscount={onRemoveDiscount} onOpenOrder={onOpenOrder} />
        },
        {
          id: "delivery",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Truck className="h-3.5 w-3.5" />
              Delivery
            </span>
          ),
          content: (
            <OrderDeliveryTab
              detail={detail}
              deliveryForm={deliveryForm}
              setDeliveryForm={setDeliveryForm}
              onSaveDelivery={onSaveDelivery}
              onGenAwbSameday={onGenAwbSameday}
            />
          )
        },
        {
          id: "products",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" />
              Products
            </span>
          ),
          content: <OrderProductsTab detail={detail} onSaveLines={onSaveLines} />
        },
        {
          id: "documents",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />
              Documents
            </span>
          ),
          content: documentsTab
        }
      ]}
    />
  );
}
